import React from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { BreadcrumbList, SEOHelmet } from 'components/SEO';
import { PLAY_PRODUCT_NAME, PLAY_SITE_BASE } from 'constants/play';
import { useGetMyTournamentsQuery } from 'store/services/play/playApi';
import type { TournamentSummary } from 'types/interfaces/play';
import { PlayLayout } from '../../layout/PlayLayout';
import { useIsAuthenticated } from '../../PlayRoutes';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { formatStartLine, tournamentStatusLabel } from '../../playFormat';
import './PlayLandingPage.css';

/**
 * /play (Decision 1, design review round 1 item 2): the organizer home.
 * Signed in: the prototype's Your events screen (Crimson Pro title, gradient
 * Create tournament CTA, event cards from GET /tournaments/mine, ruling E1).
 * Signed out: the same title treatment over the product pitch, indexable.
 */
const PlayLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { data: mine, isLoading } = useGetMyTournamentsQuery(undefined, { skip: !isAuthenticated });

  // Signed out, /play/host shows the Play auth card itself (Eric's ruling 31).
  const create = () => navigate('/play/host');

  const organized = mine?.organized ?? [];
  const joined = mine?.joined ?? [];
  const hasEvents = organized.length > 0 || joined.length > 0;

  return (
    <PlayLayout>
      <SEOHelmet
        title="TradeMagic Play - Host a Magic tournament from your phone"
        description="Create a Magic: The Gathering tournament, share one link, and run pairings, the round clock, results and standings from your phone. Free with TradeMagic."
        canonical={`${PLAY_SITE_BASE}/play`}
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: `${PLAY_SITE_BASE}/` },
          { name: PLAY_PRODUCT_NAME, url: `${PLAY_SITE_BASE}/play` },
        ]}
      />

      <div className="play-landing">
        {isAuthenticated ? (
          <h1 className="play-serif-title" id="play-your-events">
            Your events
          </h1>
        ) : (
          <>
            <h1 className="play-serif-title">Run your event from your phone</h1>
            <p className="play-helper">
              Create a tournament, share one link, and handle pairings, the round clock, results and standings without paper or a
              laptop. Players join in under a minute with Discord or their TradeMagic login.
            </p>
          </>
        )}

        <PlayPrimaryButton label="Create tournament" icon={faPlus} onClick={create} testId="play-create-cta" />

        {isAuthenticated && (
          <section className="play-landing-events" aria-labelledby="play-your-events" data-testid="play-your-events">
            {isLoading && <p className="play-helper">Loading your events…</p>}
            {!isLoading && !hasEvents && <p className="play-helper">Tournaments you host or join show up here.</p>}
            {organized.length > 0 && <EventList label="Hosting" items={organized} />}
            {joined.length > 0 && <EventList label="Playing" items={joined} />}
          </section>
        )}

        <section className="play-landing-steps" aria-label="How it works">
          <Step n={1} title="Create" body="Name, time, place, format and structure in one screen. You get a link and a QR code." />
          <Step n={2} title="Pair and start" body="Swiss pairings with a tap. Every player gets their table the moment the round starts." />
          <Step n={3} title="Report and rank" body="Players report from their phones. Standings and tiebreakers update live." />
        </section>

        {!isAuthenticated && <p className="play-helper play-landing__hint">Have an invite link? Open it to join your event.</p>}
      </div>
    </PlayLayout>
  );
};

const Step: React.FC<{ n: number; title: string; body: string }> = ({ n, title, body }) => (
  <div className="play-card play-landing-step">
    <span className="play-landing-step__n">{n}</span>
    <div>
      <h3 className="play-landing-step__title">{title}</h3>
      <p className="play-helper">{body}</p>
    </div>
  </div>
);

const chipClass = (status: TournamentSummary['status']): string => {
  if (status === 'LIVE') return 'play-status-chip play-status-chip--live';
  if (status === 'COMPLETE') return 'play-status-chip';
  return 'play-status-chip play-status-chip--purple';
};

const EventList: React.FC<{ label: string; items: TournamentSummary[] }> = ({ label, items }) => (
  <div className="play-landing-events__group">
    <p className="play-section-label">{label}</p>
    <ul className="play-landing-events__list">
      {items.map((t) => (
        <li key={t.id}>
          <Link to={`/play/${t.inviteCode}`} className={`play-card play-landing-event ${t.status === 'COMPLETE' ? 'play-landing-event--past' : ''}`} data-testid="play-event-row">
            <div className="play-landing-event__top">
              <span className="play-landing-event__name">{t.name}</span>
              <FontAwesomeIcon icon={faChevronRight} className="play-landing-event__chevron" />
            </div>
            <div className="play-landing-event__meta">
              <span className={chipClass(t.status)}>{tournamentStatusLabel(t.status, t.currentRoundNumber, t.swissRounds)}</span>
              <span className="play-landing-event__facts">
                {formatStartLine(t.startsAt)} · {t.formatLabel} · {t.registeredCount} registered
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default PlayLandingPage;
